import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Button,
  Field,
  Input,
  Select,
  centsFromInput,
  formatMoney,
  inputFromCents,
} from "./ui";

/**
 * Configuring what a marketer earns.
 *
 * A payout scheme is easy to get subtly wrong and expensive to get wrong
 * quietly, so this screen is built around one idea: **you should be able to
 * read back what you just configured, and see what it pays, before you save
 * it.** Two things follow from that.
 *
 * The worked example is answered by the server, not computed here. The obvious
 * shortcut would be to do the arithmetic in the browser, but then the number
 * somebody reads while deciding would come from an implementation nobody tests,
 * beside a payout that comes from one that is. `POST /admin/commission/preview`
 * accepts the *unsaved* rule for exactly this reason.
 *
 * And the rule is read back as a sentence — "20% of the sale, never below
 * $100.00" — because a list of thresholds and basis points is not something
 * anybody proofreads successfully.
 */

export type TierSelector = "one_time" | "monthly";

export interface RuleTier {
  on: TierSelector;
  minCents: number;
  percentBps?: number;
  flatCents?: number;
}

export interface Rule {
  base: "one_time";
  tiers: RuleTier[];
  minCents?: number;
}

interface Preview {
  allowed: boolean;
  reason: string | null;
  commissionCents: number;
  rule: string;
  currency: string | null;
}

/** A tier as the form holds it — strings, because half-typed numbers are normal. */
interface DraftTier {
  on: TierSelector;
  threshold: string;
  payout: "percent" | "flat";
  percent: string;
  flat: string;
}

function draftFrom(tier: RuleTier): DraftTier {
  return {
    on: tier.on,
    threshold: inputFromCents(tier.minCents),
    payout: tier.flatCents === undefined ? "percent" : "flat",
    percent: tier.percentBps === undefined ? "" : String(tier.percentBps / 100),
    flat: inputFromCents(tier.flatCents),
  };
}

const BLANK: DraftTier = { on: "one_time", threshold: "", payout: "percent", percent: "", flat: "" };

/** Null when the draft is not yet a rule — mid-typing is the normal state. */
export function ruleFromDrafts(tiers: DraftTier[], minimum: string): Rule | null {
  if (tiers.length === 0) return null;

  const built: RuleTier[] = [];
  for (const tier of tiers) {
    const minCents = centsFromInput(tier.threshold) ?? 0;
    if (tier.payout === "flat") {
      const flatCents = centsFromInput(tier.flat);
      if (flatCents === undefined) return null;
      built.push({ on: tier.on, minCents, flatCents });
    } else {
      const percent = Number(tier.percent);
      if (tier.percent.trim() === "" || Number.isNaN(percent)) return null;
      // Percentages are entered as percent and stored as basis points, so 12.5%
      // survives as 1250 rather than as a float nobody can round-trip.
      built.push({ on: tier.on, minCents, percentBps: Math.round(percent * 100) });
    }
  }

  const minCents = centsFromInput(minimum);
  return { base: "one_time", tiers: built, ...(minCents === undefined ? {} : { minCents }) };
}

export function CommissionRuleEditor({
  planId,
  value,
  listPriceCents,
  monthlyCents,
  floorPriceCents,
  onChange,
}: {
  /** Present when editing a saved plan; absent while creating one. */
  planId?: string | undefined;
  value: Rule | null;
  /** What a sale at list would be, so the worked example uses a real number. */
  listPriceCents: number | undefined;
  monthlyCents: number | undefined;
  floorPriceCents: number | undefined;
  onChange: (rule: Rule | null) => void;
}) {
  const [tiers, setTiers] = useState<DraftTier[]>(
    value === null || value.tiers.length === 0 ? [] : value.tiers.map(draftFrom),
  );
  const [minimum, setMinimum] = useState(inputFromCents(value?.minCents));
  // The example price an admin is checking against. Starts at list, because
  // "what does a full-price sale pay" is the first question anybody asks.
  const [testPrice, setTestPrice] = useState("");

  const rule = ruleFromDrafts(tiers, minimum);

  // Report upwards on every edit so the parent form always holds the current
  // rule — no separate "apply" step to forget.
  useEffect(() => {
    onChange(rule);
    // `rule` is rebuilt each render; comparing its JSON avoids an update loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rule)]);

  const exampleCents = centsFromInput(testPrice) ?? listPriceCents;

  const preview = useQuery({
    queryKey: ["commission-preview", planId ?? null, JSON.stringify(rule), exampleCents, monthlyCents, floorPriceCents],
    enabled: rule !== null && exampleCents !== undefined,
    queryFn: () =>
      api<Preview>("/api/v1/admin/commission/preview", {
        method: "POST",
        body: {
          ...(planId === undefined ? {} : { planId }),
          rule,
          oneTimeCents: exampleCents,
          ...(monthlyCents === undefined ? {} : { monthlyCents }),
          ...(floorPriceCents === undefined ? {} : { floorPriceCents }),
        },
      }),
  });

  return (
    <fieldset className="space-y-3 border-0 p-0">
      <legend className="field-label">Marketer commission</legend>

      {tiers.length === 0 ? (
        <p className="text-sm text-dim">
          No commission on this plan. Add a tier to pay a marketer for selling it.
        </p>
      ) : (
        <div className="space-y-3">
          {tiers.map((tier, index) => (
            <div key={index} className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <Field label="Decided by">
                <Select
                  value={tier.on}
                  onChange={(event) =>
                    setTiers(replace(tiers, index, { on: event.target.value as TierSelector }))
                  }
                >
                  <option value="one_time">The sale price</option>
                  <option value="monthly">The monthly rate</option>
                </Select>
              </Field>

              <Field label="From" hint="Inclusive.">
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={tier.threshold}
                  placeholder="0.00"
                  onChange={(event) =>
                    setTiers(replace(tiers, index, { threshold: event.target.value }))
                  }
                />
              </Field>

              <Field label="Pays">
                <Select
                  value={tier.payout}
                  onChange={(event) =>
                    setTiers(
                      replace(tiers, index, { payout: event.target.value as "percent" | "flat" }),
                    )
                  }
                >
                  <option value="percent">A percentage</option>
                  <option value="flat">A flat amount</option>
                </Select>
              </Field>

              {tier.payout === "percent" ? (
                <Field label="Percent">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={tier.percent}
                    placeholder="20"
                    onChange={(event) =>
                      setTiers(replace(tiers, index, { percent: event.target.value }))
                    }
                  />
                </Field>
              ) : (
                <Field label="Amount">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={tier.flat}
                    placeholder="0.00"
                    onChange={(event) =>
                      setTiers(replace(tiers, index, { flat: event.target.value }))
                    }
                  />
                </Field>
              )}

              <div className="flex items-end pb-1">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setTiers(tiers.filter((_, at) => at !== index))}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Button type="button" onClick={() => setTiers([...tiers, { ...BLANK }])}>
          Add tier
        </Button>

        <Field label="Never below" hint="Optional. A floor on the payout itself.">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={minimum}
            placeholder="0.00"
            onChange={(event) => setMinimum(event.target.value)}
          />
        </Field>

        <Field label="Check a sale of" hint="Only for the example below.">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={testPrice}
            placeholder={inputFromCents(listPriceCents) || "0.00"}
            onChange={(event) => setTestPrice(event.target.value)}
          />
        </Field>
      </div>

      {/* Read back what was just configured, before it is saved. A list of
          thresholds and basis points is not something anybody proofreads. */}
      {rule !== null && preview.data !== undefined && (
        <div className="space-y-1 border-l-2 border-accent/40 pl-3">
          <p className="text-sm text-muted">{preview.data.rule}</p>
          {preview.data.allowed ? (
            <p className="text-sm text-ink">
              A sale of{" "}
              <span className="tabular">
                {formatMoney(exampleCents ?? 0, preview.data.currency)}
              </span>{" "}
              pays the marketer{" "}
              <span className="tabular font-semibold">
                {formatMoney(preview.data.commissionCents, preview.data.currency)}
              </span>
              .
            </p>
          ) : (
            <p className="text-sm text-pending">{preview.data.reason}</p>
          )}
        </div>
      )}

      {rule === null && tiers.length > 0 && (
        <p className="text-sm text-dim">Finish the tiers above to see what they pay.</p>
      )}
    </fieldset>
  );
}

function replace(tiers: DraftTier[], index: number, patch: Partial<DraftTier>): DraftTier[] {
  return tiers.map((tier, at) => (at === index ? { ...tier, ...patch } : tier));
}
